import {UserProfileFragment} from "../generated/graphql";
import {parseCookies, setCookie, destroyCookie} from "nookies";
import {useCallback, useEffect, useState} from "react";
import {useSnackbar} from "notistack";
import {useApollo} from "../lib/apolloClient/frontend";
import {getRolePermissions, getUserPermissions, hasPermission} from "../config/permissions";
import {useProfile} from "@/components/Auth/ProfileProvider";

const IMPERSONATE_COOKIE_NAME = "__impersonate";
const IMPERSONATE_SYNC_STORAGE_KEY = "__impersonate_sync_v1";

export function applicationLinkForContact({companyName, email}: { companyName: string, email: string }) {
  return `https://yrzrxhm0qtt.typeform.com/to/pdc7DMta#company=${encodeURIComponent(companyName)}&contact=${encodeURIComponent(email)}`
}

export function useAuth() {
  const {profile, loading, refetch} = useProfile()
  const cookies = parseCookies()
  const impersonatedUserId = cookies[IMPERSONATE_COOKIE_NAME]
  // `__impersonate` is a user id, not an email. If it's set, we're impersonating.
  const isImpersonated = Boolean(impersonatedUserId)
  const userPermissions = getUserPermissions(profile as UserProfileFragment)
  const rolePermissions = getRolePermissions(profile as UserProfileFragment)
  const _hasPermission = useCallback((p: string) => hasPermission(profile as UserProfileFragment, p), [profile])

  const hasCultivation = profile?.organization?.moduleEnabledCultivation
  const hasProcessing = profile?.organization?.moduleEnabledProcessing
  const hasFinance = profile?.organization?.moduleEnabledFinance
  const hasMaintenance = profile?.organization?.moduleEnabledMaintenance

  return {
    loading,
    currentUser: profile,
    isImpersonated,
    hasPermission: _hasPermission,
    userPermissions,
    rolePermissions,
    // We don't currently expose "who is impersonating" on the `UserProfile` fragment.
    // Keep this key for backwards compatibility with existing components.
    impersonator: undefined as unknown as string | undefined,
    hasCultivation,
    hasProcessing,
    hasFinance,
    hasMaintenance,
    refetch
  }
}

export function useTracking() {
  const [tracked, setTracked] = useState(false)
  const {isImpersonated} = useAuth()
  return useCallback((name: string) => {
    const interval = setInterval(() => {
      if (tracked && interval)
        clearInterval(interval)
      if (window.ChurnZero?.ready && !tracked && process.env.NODE_ENV === "production" && !isImpersonated) {
        console.info("ChurnZero Track Event:", name)
        window.ChurnZero?.push(['trackEvent', name])
        setTracked(true)
        clearInterval(interval)
      }
    }, 2000)
    return () => clearInterval(interval)
  }, [tracked])
}

export function useTrackPageLoad(pageName: string) {
  const TrackEvent = useTracking()
  useEffect(() => {
    TrackEvent(`${pageName}.PageLoad`)
  }, [])
}

export function useImpersonateUser() {
  const client = useApollo({});
  const { currentUser } = useAuth();
  const [impersonating, setImpersonating] = useState(() => {
    const cookies = parseCookies()
    return cookies[IMPERSONATE_COOKIE_NAME] || ""
  })
  const {enqueueSnackbar} = useSnackbar()

  const syncFromCookie = useCallback(() => {
    const cookies = parseCookies()
    const cookieValue = cookies[IMPERSONATE_COOKIE_NAME] || ""
    setImpersonating(cookieValue)
    return cookieValue
  }, [])

  // Keep multiple tabs in sync: when any tab begins/ends impersonation, it writes to localStorage.
  // Other tabs receive a `storage` event, update their local state, and reset Apollo cache.
  useEffect(() => {
    // Ensure our initial state matches current cookie value
    syncFromCookie()

    if (typeof window === "undefined") return

    const onStorage = async (e: StorageEvent) => {
      if (e.key !== IMPERSONATE_SYNC_STORAGE_KEY) return
      const cookieValue = syncFromCookie()
      // Apollo cache may contain data for the previous user; clear it so subsequent queries refetch.
      try {
        await client.clearStore()
      } catch {
        // ignore
      }
      if (cookieValue) {
        enqueueSnackbar(`Impersonation updated in another tab`, {variant: "info"})
      } else {
        enqueueSnackbar(`Impersonation ended in another tab`, {variant: "info"})
      }
      // Move the user back to the correct entry point for their (possibly new) identity.
      // Without this, they can remain on a stale/unauthorized page until they manually navigate.
      if (!window.location.pathname.startsWith("/auth/")) {
        window.location.assign("/app")
      }
    }

    window.addEventListener("storage", onStorage)
    return () => window.removeEventListener("storage", onStorage)
  }, [client, enqueueSnackbar, syncFromCookie])

  const broadcastImpersonationChange = useCallback((nextUserId: string | null) => {
    if (typeof window === "undefined") return
    try {
      // Some parts of the app expect this key when impersonating.
      if (nextUserId) window.localStorage.setItem("impersonatedUserId", nextUserId)
      else window.localStorage.removeItem("impersonatedUserId")

      window.localStorage.setItem(
        IMPERSONATE_SYNC_STORAGE_KEY,
        JSON.stringify({at: Date.now(), userId: nextUserId})
      )
    } catch {
      // ignore
    }
  }, [])

  const endImpersonate = useCallback(async () => {
    destroyCookie(null, IMPERSONATE_COOKIE_NAME, {path: "/"})
    setImpersonating("")
    broadcastImpersonationChange(null)
    enqueueSnackbar(`Ended impersonation`, {variant: "success"})
    enqueueSnackbar(`Please be patient, switching users takes a moment...`)
    // Avoid `resetStore()` here: it refetches active queries which can fail during identity changes,
    // and that would prevent us from navigating away.
    try {
      await client.clearStore()
    } catch {
      // ignore
    } finally {
      // Redirect directly to organization-workspace - impersonation is only
      // available from the admin workspace, so we always return there
      window.location.replace("/app/admin/organization-workspace")
    }
  }, [broadcastImpersonationChange, client, enqueueSnackbar])

  const beginImpersonate = useCallback(async (userId: string, days = 1, destination = "/app") => {
    if (!userId) {
      return await endImpersonate();
    }

    setCookie(null, IMPERSONATE_COOKIE_NAME, userId, {
      maxAge: 24 * 60 * 60 * days,
      path: '/'
    });
    
    setImpersonating(userId);
    broadcastImpersonationChange(userId)
    enqueueSnackbar(`Beginning to impersonate ${userId}`, {variant: "success"});
    enqueueSnackbar(`Please be patient, switching users takes a moment...`);
    // Same rationale as `endImpersonate`: don't let refetch failures block navigation.
    try {
      await client.clearStore();
    } catch {
      // ignore
    } finally {
      window.location.assign(destination);
    }
  }, [broadcastImpersonationChange, client, endImpersonate, enqueueSnackbar]);

  

  return {impersonating, endImpersonate, beginImpersonate}
}
